/**
 * Change Role Modal Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Modal dialog for changing a user's role.
 */

import React, { useState, useEffect } from 'react';
import { X, Shield } from 'lucide-react';
import type { UserListItem, UserRole } from '../../../types/admin';
import { ROLE_LABELS } from '../../../types/admin';
import UserRoleSelect from './UserRoleSelect';
import { Button } from '../../ui/button';

interface ChangeRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userId: string, role: UserRole) => Promise<void>;
  user: UserListItem | null;
  isLoading?: boolean;
}

const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  isLoading = false,
}) => {
  const [selectedRole, setSelectedRole] = useState<UserRole>('advisor');

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen && user) {
      setSelectedRole(user.role);
    }
  }, [isOpen, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    await onSubmit(user.id, selectedRole);
  };

  if (!isOpen || !user) return null;

  const hasChanged = selectedRole !== user.role;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Shield size={20} className="text-[#002B45]" />
              <h2 className="text-lg font-semibold text-gray-900">Change Role</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              {/* User info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">Changing role for:</p>
                <p className="font-medium text-gray-900">
                  {user.display_name || user.email}
                </p>
                <p className="text-sm text-gray-500">
                  Current role: {ROLE_LABELS[user.role]}
                </p>
              </div>

              {/* Role selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Role
                </label>
                <UserRoleSelect
                  value={selectedRole}
                  onChange={setSelectedRole}
                  showDescription
                />
              </div>

              {/* Warning for role change */}
              {hasChanged && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    Changing this user's role will immediately affect their permissions and access to features.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !hasChanged}
              >
                {isLoading ? 'Saving...' : 'Change Role'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangeRoleModal;
